import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import {
  Package,
  Eye,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Building,
  RotateCcw,
  User,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  X,
  TrendingUp,
  ChevronDown,
  CreditCard,
  Phone,
  Mail,
  Clock
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card'
import InlineLoader from '../../../components/ui/InlineLoader'
import customerReturnService from '../../../services/customerReturnService'
import branchService from '../../../services/branchService'
import AuthService from '../../../services/authService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <AlertCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 text-white/80 hover:text-white">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const CustomerReturn = () => {
  // Authentication
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id

  // State
  const [customerReturns, setCustomerReturns] = useState([])
  const [filteredCustomerReturns, setFilteredCustomerReturns] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState(isManager ? (userBranchId || '') : '')
  const [statusFilter, setStatusFilter] = useState('')
  const [toast, setToast] = useState(null)
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedCustomerReturn, setSelectedCustomerReturn] = useState(null)
  
  // Additional data
  const [branches, setBranches] = useState([])
  const [availableReasons, setAvailableReasons] = useState([])

  // Toast helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  // Data loading functions
  const loadCustomerReturns = useCallback(async () => {
    try {
      setLoading(true)
      console.log('� Loading customer returns...')
      
      const response = await customerReturnService.getAllCustomerReturns()
      console.log('📦 Customer returns response:', response)
      
      if (response.success && response.data) {
        // Handle different response structures
        let customerReturnsData = []
        
        if (Array.isArray(response.data)) {
          customerReturnsData = response.data
        } else if (response.data.orderReturns && Array.isArray(response.data.orderReturns)) {
          customerReturnsData = response.data.orderReturns
        } else if (response.data.customerReturns && Array.isArray(response.data.customerReturns)) {
          customerReturnsData = response.data.customerReturns
        } else if (response.data.returns && Array.isArray(response.data.returns)) {
          customerReturnsData = response.data.returns
        } else {
          console.warn('⚠️ Unable to find customer returns array in response.data:', response.data)
          console.log('📋 Available keys in response.data:', Object.keys(response.data))
        }
        
        console.log('📦 Extracted customer returns data:', customerReturnsData)
        setCustomerReturns(customerReturnsData)
        
        // Extract unique reason types from the response
        const reasons = [...new Set(customerReturnsData
          .map(customerReturn => customerReturn.reasonType)
          .filter(reason => reason && reason.trim() !== '')
        )]
        
        console.log('📋 Available reason types:', reasons)
        setAvailableReasons(reasons)
        
        showToast(`Loaded ${customerReturnsData.length} customer returns`, 'success')
      } else {
        console.warn('⚠️ Invalid response format:', response)
        setCustomerReturns([])
        setAvailableReasons([])
        showToast('No customer returns found', 'info')
      }
    } catch (error) {
      console.error('❌ Error loading customer returns:', error)
      showToast('Failed to load customer returns', 'error')
      setCustomerReturns([])
      setAvailableReasons([])
    } finally {
      setLoading(false)
    }
  }, [])

  const loadBranches = useCallback(async () => {
    try {
      const response = await branchService.getAllBranches()
      if (response.success) {
        setBranches(response.data?.branches || [])
      }
    } catch (error) {
      console.error('Error loading branches:', error)
      setBranches([])
    }
  }, [])

  // Filter customer returns
  const applyFilters = useCallback(() => {
    let filtered = [...customerReturns]

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(customerReturn =>
        customerReturn._id?.toLowerCase().includes(search) ||
        customerReturn.order?.orderNumber?.toLowerCase().includes(search) ||
        customerReturn.order?.customer?.name?.toLowerCase().includes(search) ||
        customerReturn.order?.customer?.mobileNumber?.includes(search) ||
        customerReturn.reasonType?.toLowerCase().includes(search)
      )
    }

    // Branch filter
    if (branchFilter) {
      filtered = filtered.filter(customerReturn => 
        customerReturn.order?.branch?._id === branchFilter
      )
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(customerReturn => 
        customerReturn.reasonType === statusFilter
      )
    }

    setFilteredCustomerReturns(filtered)
  }, [customerReturns, searchTerm, branchFilter, statusFilter])

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    // Managers cannot clear their branch filter
    if (!isManager) {
      setBranchFilter('')
    }
    setStatusFilter('')
  }

  // Check if any filters are active (exclude manager's locked branch filter)
  const hasActiveFilters = searchTerm.trim() || (!isManager && branchFilter) || statusFilter

  // Load data
  useEffect(() => {
    const loadData = async () => {
      await loadCustomerReturns()
      await loadBranches()
    }
    loadData()
  }, [loadCustomerReturns, loadBranches])

  // Apply filters
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  // Modal handlers
  const openViewModal = (customerReturn) => {
    setSelectedCustomerReturn(customerReturn)
    setShowViewModal(true)
  }

  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'N/A'
    return format(new Date(date), 'MMM dd, yyyy')
  }

  const formatCurrency = (amount) => {
    return `Rs. ${new Intl.NumberFormat('en-IN').format(amount || 0)}`
  }

  const getStatusBadge = (reasonType) => {
    if (!reasonType) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      )
    }

    // Dynamic color assignment based on reason type
    const getReasonColor = (reason) => {
      const colors = [
        { bg: 'bg-blue-100', text: 'text-blue-800' },
        { bg: 'bg-red-100', text: 'text-red-800' },
        { bg: 'bg-orange-100', text: 'text-orange-800' },
        { bg: 'bg-yellow-100', text: 'text-yellow-800' },
        { bg: 'bg-green-100', text: 'text-green-800' },
        { bg: 'bg-purple-100', text: 'text-purple-800' },
        { bg: 'bg-pink-100', text: 'text-pink-800' },
        { bg: 'bg-indigo-100', text: 'text-indigo-800' }
      ]
      
      // Use a simple hash to consistently assign colors
      const hash = reason.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      return colors[hash % colors.length]
    }
    
    const colorConfig = getReasonColor(reasonType)
    const label = reasonType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colorConfig.bg} ${colorConfig.text}`}>
        {label}
      </span>
    )
  }

  // Get statistics
  const getStatistics = () => {
    const total = customerReturns.length
    const today = new Date()
    const thisMonth = customerReturns.filter(customerReturn => {
      const returnDate = new Date(customerReturn.createdAt)
      return returnDate.getMonth() === today.getMonth() && 
             returnDate.getFullYear() === today.getFullYear()
    }).length

    const totalAmount = customerReturns.reduce((sum, customerReturn) => 
      sum + (customerReturn.totalAmount || 0), 0
    )

    const avgAmount = total > 0 ? totalAmount / total : 0

    return { total, thisMonth, totalAmount, avgAmount }
  }

  const statistics = getStatistics()

  return (
    <div className="space-y-6">
      <div className="bg-white">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">

              Customer Returns
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={loadCustomerReturns}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Returns */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <RotateCcw className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Returns</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* This Month */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.thisMonth}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Amount */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <DollarSign className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Refunded</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Return */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Return</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.avgAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header with Clear Button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">Filter Customer Returns</h3>
              </div>
              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-orange-300 text-orange-500 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200"
                >
                  Clear Filter
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input
                  placeholder="Search returns, orders, customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 h-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* Branch Filter */}
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                {isManager ? (
                  // Manager: Read-only branch display
                  <div className="w-full pl-10 pr-4 py-2.5 h-10 border border-gray-300 rounded-md bg-gray-50 text-sm flex items-center text-gray-700 font-medium">
                    {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
                  </div>
                ) : (
                  // Admin: Branch dropdown
                  <>
                    <select
                      value={branchFilter}
                      onChange={(e) => setBranchFilter(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-sm"
                    >
                      <option value="">All Branches</option>
                      {branches.map(branch => (
                        <option key={branch._id} value={branch._id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </>
                )}
              </div>

              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-sm"
                >
                  <option value="">All Reasons</option>
                  {availableReasons.map(reason => (
                    <option key={reason} value={reason}>
                      {reason.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            {/* Results Count */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredCustomerReturns.length} of {customerReturns.length} customer returns
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Customer Returns List */}
        <Card>
          {/* <CardHeader>
            <CardTitle className="flex items-center">
              <RotateCcw className="h-5 w-5 text-blue-600 mr-2" />
              <span>Customer Returns</span>
            </CardTitle>
          </CardHeader> */}
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <InlineLoader />
                <span className="ml-3 text-gray-600">Loading customer returns...</span>
              </div>
            ) : filteredCustomerReturns.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Return Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Return #
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Original Order
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Branch
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Refund Amount
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCustomerReturns.map((customerReturn) => (
                        <tr key={customerReturn._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {formatDate(customerReturn.createdAt)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <RotateCcw className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{customerReturn._id.slice(-8)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-gray-900">
                              {customerReturn.order?.orderNumber || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-gray-900 font-medium">
                                  {customerReturn.order?.customer?.name || 'N/A'}
                                </p>
                                <p className="text-gray-500 text-sm">
                                  {customerReturn.order?.customer?.mobileNumber || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {customerReturn.order?.branch?.name || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(customerReturn.reasonType)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">
                                {formatCurrency(customerReturn.totalAmount)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => openViewModal(customerReturn)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="View Return"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-4 p-4">
                  {filteredCustomerReturns.map((customerReturn) => (
                    <div
                      key={customerReturn._id}
                      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <RotateCcw className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{customerReturn._id.slice(-8)}</p>
                            <p className="text-sm text-gray-500">Order: {customerReturn.order?.orderNumber || 'N/A'}</p>
                          </div>
                        </div>
                        {getStatusBadge(customerReturn.reasonType)}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Return Date</p>
                          <p className="text-gray-900">{formatDate(customerReturn.createdAt)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Refund Amount</p>
                          <p className="text-gray-900 font-medium">{formatCurrency(customerReturn.totalAmount)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Customer</p>
                          <p className="text-gray-900 truncate">{customerReturn.order?.customer?.name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Branch</p>
                          <p className="text-gray-900">{customerReturn.branch?.name || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            Processed by {customerReturn.processedBy?.name || 'N/A'}
                          </span>
                        </div>

                        <button
                          onClick={() => openViewModal(customerReturn)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                          title="View Return"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <RotateCcw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Customer Returns Found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || branchFilter || statusFilter
                    ? 'No customer returns match your current filters. Try adjusting your search criteria.'
                    : 'There are no customer returns to display at the moment.'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>

      {/* View Modal */}
      {showViewModal && selectedCustomerReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <RotateCcw className="h-5 w-5 text-blue-600 mr-2" />
                Customer Return Details
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Number</label>
                    <p className="text-gray-900 font-medium">{selectedCustomerReturn._id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Original Order</label>
                    <p className="text-gray-900">{selectedCustomerReturn.order?.orderNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                    <p className="text-gray-900">{formatDate(selectedCustomerReturn.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    {getStatusBadge(selectedCustomerReturn.reasonType)}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                    <div className="space-y-1">
                      <p className="text-gray-900 font-medium flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        {selectedCustomerReturn.order?.customer?.name || 'N/A'}
                      </p>
                      <p className="text-gray-600 flex items-center text-sm">
                        <Phone className="h-4 w-4 text-gray-400 mr-2" />
                        {selectedCustomerReturn.order?.customer?.mobileNumber || 'N/A'}
                      </p>
                      <p className="text-gray-600 flex items-center text-sm">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        {selectedCustomerReturn.order?.customer?.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                    <p className="text-gray-900 flex items-center">
                      <Building className="h-4 w-4 text-gray-400 mr-2" />
                      {selectedCustomerReturn.order?.branch?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processed By</label>
                    <p className="text-gray-900">{selectedCustomerReturn.createdBy?.username || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Return Details */}
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Reason</label>
                    <p className="text-gray-900">{selectedCustomerReturn.reasonType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method</label>
                    <p className="text-gray-900 flex items-center">
                      <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                      {selectedCustomerReturn.refundMethod?.charAt(0).toUpperCase() + selectedCustomerReturn.refundMethod?.slice(1) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Returned Items */}
              {selectedCustomerReturn.items && selectedCustomerReturn.items.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Returned Items</h4>
                  <div className="space-y-3">
                    {selectedCustomerReturn.items.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{item.product?.name || 'Unknown Product'}</h5>
                            <p className="text-sm text-gray-500 mb-2">SKU: {item.product?.sku || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Discount: Rs. {item.discountPerUnit || 0}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-sm text-gray-500">Qty: {item.returnQuantity}</p>
                            <p className="text-sm text-gray-500">Unit: {formatCurrency(item.unitPrice)}</p>
                            <p className="font-medium text-gray-900">Total: {formatCurrency(item.refundAmount)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-gray-900">Total Refund Amount:</span>
                      <span className="text-xl font-bold text-gray-900">{formatCurrency(selectedCustomerReturn.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Waste Cost</label>
                    <p className="text-gray-900">{formatCurrency(selectedCustomerReturn.wasteCost || 0)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Restockable</label>
                    <p className="text-gray-900">{selectedCustomerReturn.restockableStatus ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  </div>
  )
}

export default CustomerReturn